import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS as DndCSS } from "@dnd-kit/utilities";
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Folder,
  FolderMinus,
  FolderPlus,
  GripVertical,
  MoreHorizontal,
  Pencil,
  Settings,
  Star,
  Trash2,
} from "lucide-react";
import { type ButtonHTMLAttributes, forwardRef, useId, useState } from "react";
import { IconButton } from "../../components/IconButton/IconButton";
import { ContextMenu, type ContextMenuItem } from "../../components/Menu/ContextMenu";
import { Tooltip } from "../../components/Tooltip/Tooltip";
import { FolderNameInput } from "./FolderNameInput";
import { folderDragId, folderDropId, spaceDragId, stepMove, stepMoveFolder } from "./folderMoves";
import { fill, type SidebarFolderHandlers, type SidebarFolderLabels, type SidebarNavSpaceFolder } from "./folderTypes";
import type { SidebarNavSpaceItem } from "./SidebarNav";
import { SidebarSpaceRow } from "./SidebarSpaceRow";

const Grip = forwardRef<HTMLButtonElement, { label: string } & ButtonHTMLAttributes<HTMLButtonElement>>(function Grip(
  { label, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      className="grid size-5 cursor-grab touch-none place-items-center rounded text-content-tertiary hover:text-content-primary btn-focus-ring active:cursor-grabbing"
      {...props}
    >
      <GripVertical size={13} />
    </button>
  );
});

function focusSpaceRow(spaceKey: string) {
  const row = document.querySelector<HTMLElement>(`[data-sidebar-space="${CSS.escape(spaceKey)}"] button`);
  row?.focus();
}

function SortableSpace({
  space,
  folder,
  folders,
  sortable,
  activeSpaceKey,
  onOpenSpace,
  onTogglePinSpace,
  onStartNewFolder,
  handlers,
  labels: l,
  starLabel,
  showStar,
}: Readonly<{
  space: SidebarNavSpaceItem;
  folder: SidebarNavSpaceFolder;
  folders: SidebarNavSpaceFolder[];
  sortable: boolean;
  activeSpaceKey?: string;
  onOpenSpace: (key: string) => void;
  onTogglePinSpace: (key: string) => void;
  onStartNewFolder: (spaceKey: string) => void;
  handlers: SidebarFolderHandlers;
  labels: SidebarFolderLabels;
  starLabel: string;
  showStar: boolean;
}>) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: spaceDragId(space.key),
    data: { type: "space" },
  });
  // After a move from the menu the row is somewhere else; keep keyboard focus on it there.
  const moveAndFocus = (folderKey: string, index: number | null) => {
    handlers.onMoveSpace(space.key, folderKey, index);
    setTimeout(() => focusSpaceRow(space.key), 0);
  };
  const removeAndFocus = () => {
    handlers.onRemoveFromFolder(space.key);
    setTimeout(() => focusSpaceRow(space.key), 0);
  };
  const step = (dir: -1 | 1) => {
    const move = stepMove(folder, space.key, dir);
    if (move?.type === "moveSpace") moveAndFocus(move.folderKey, move.index);
  };
  const index = folder.spaceKeys.indexOf(space.key);
  const items: ContextMenuItem[] = [
    ...(showStar ? [] : [{ label: starLabel, icon: <Star size={14} />, onSelect: () => onTogglePinSpace(space.key) }]),
    ...(handlers.onOpenSpaceSettings
      ? [
          {
            label: l.spaceSettings,
            icon: <Settings size={14} />,
            onSelect: () => handlers.onOpenSpaceSettings?.(space.key),
          },
        ]
      : []),
    ...(sortable && index > 0
      ? [{ label: l.moveUp, icon: <ArrowUp size={14} />, group: l.moveGroup, onSelect: () => step(-1) }]
      : []),
    ...(sortable && index < folder.spaceKeys.length - 1
      ? [{ label: l.moveDown, icon: <ArrowDown size={14} />, group: l.moveGroup, onSelect: () => step(1) }]
      : []),
    ...folders
      .filter((s) => s.key !== folder.key)
      .map((s) => ({
        label: fill(l.moveTo, s.name),
        icon: <Folder size={14} />,
        group: l.moveToGroup,
        onSelect: () => moveAndFocus(s.key, null),
      })),
    {
      label: l.moveToNewFolder,
      icon: <FolderPlus size={14} />,
      group: l.moveToGroup,
      onSelect: () => onStartNewFolder(space.key),
    },
    {
      label: l.removeFromFolder,
      icon: <FolderMinus size={14} />,
      group: "",
      onSelect: removeAndFocus,
    },
  ];
  return (
    <div
      ref={setNodeRef}
      data-sidebar-space={space.key}
      style={{ transform: DndCSS.Translate.toString(transform), transition }}
      className={isDragging ? "relative opacity-40" : undefined}
    >
      <SidebarSpaceRow
        space={space}
        indented
        showStar={showStar}
        active={space.key === activeSpaceKey}
        starLabel={starLabel}
        onOpen={() => onOpenSpace(space.key)}
        onToggleStar={() => onTogglePinSpace(space.key)}
        menu={{ label: l.spaceMenu, ariaLabel: fill(l.spaceMenuAria, space.name), items }}
        dragHandle={
          <Grip label={fill(l.dragSpace, space.name)} ref={setActivatorNodeRef} {...attributes} {...listeners} />
        }
      />
    </div>
  );
}

export function SidebarSpaceFolder({
  folder,
  folders,
  spaces,
  activeSpaceKey,
  onOpenSpace,
  onTogglePinSpace,
  onStartNewFolder,
  handlers,
  labels: l,
  starLabel,
  unpinLabel,
  showStar,
}: Readonly<{
  folder: SidebarNavSpaceFolder;
  folders: SidebarNavSpaceFolder[];
  spaces: Map<string, SidebarNavSpaceItem>;
  activeSpaceKey?: string;
  onOpenSpace: (key: string) => void;
  onTogglePinSpace: (key: string) => void;
  onStartNewFolder: (spaceKey: string) => void;
  handlers: SidebarFolderHandlers;
  labels: SidebarFolderLabels;
  starLabel: string;
  unpinLabel: string;
  showStar: boolean;
}>) {
  const [renaming, setRenaming] = useState(false);
  const listId = useId();
  const custom = folder.kind === "custom";
  const sortable = folder.sortMode === "custom";
  const header = useSortable({ id: folderDragId(folder.key), data: { type: "folder" }, disabled: !custom });
  const drop = useDroppable({ id: folderDropId(folder.key), data: { type: "drop" } });
  const members = folder.spaceKeys.map((k) => spaces.get(k)).filter((s): s is SidebarNavSpaceItem => !!s);

  const customKeys = folders.filter((s) => s.kind === "custom").map((s) => s.key);
  const customIndex = customKeys.indexOf(folder.key);
  const stepFolder = (dir: -1 | 1) => {
    const move = stepMoveFolder(folders, folder.key, dir);
    if (move?.type === "reorderFolders") handlers.onReorderFolders(move.folderKeys);
  };

  const sort = (mode: SidebarNavSpaceFolder["sortMode"], label: string): ContextMenuItem => ({
    label,
    group: l.sortGroup,
    selected: folder.sortMode === mode,
    onSelect: () => handlers.onSetFolderSort(folder.key, mode),
  });
  const items: ContextMenuItem[] = [
    ...(custom ? [{ label: l.rename, icon: <Pencil size={14} />, onSelect: () => setRenaming(true) }] : []),
    ...(custom && customIndex > 0
      ? [{ label: l.moveUp, icon: <ArrowUp size={14} />, group: l.moveGroup, onSelect: () => stepFolder(-1) }]
      : []),
    ...(custom && customIndex < customKeys.length - 1
      ? [{ label: l.moveDown, icon: <ArrowDown size={14} />, group: l.moveGroup, onSelect: () => stepFolder(1) }]
      : []),
    sort("custom", l.sortCustom),
    sort("alpha", l.sortAlpha),
    sort("recent", l.sortRecent),
    ...(custom
      ? [
          {
            label: l.removeFolder,
            icon: <Trash2 size={14} />,
            danger: true,
            group: "",
            onSelect: () => handlers.onDeleteFolder(folder.key),
          },
        ]
      : []),
  ];

  return (
    <div
      ref={header.setNodeRef}
      style={{ transform: DndCSS.Translate.toString(header.transform), transition: header.transition }}
      className={header.isDragging ? "relative z-20 opacity-60" : undefined}
    >
      <div ref={drop.setNodeRef} className={["rounded-lg", drop.isOver ? "bg-surface-base" : ""].join(" ")}>
        <ContextMenu items={items}>
          {(openFrom, menuOpen) => (
            <div className="group relative flex w-full items-center gap-3 rounded-lg py-2 pr-2 pl-3 text-content-secondary transition-colors hover:bg-surface-base hover:text-content-primary [font:var(--type-body-xs)]">
              {renaming ? (
                <div className="min-w-0 flex-1">
                  <FolderNameInput
                    initial={folder.name}
                    label={l.folderName}
                    placeholder={l.folderNamePlaceholder}
                    onSave={(name) => {
                      setRenaming(false);
                      handlers.onRenameFolder(folder.key, name);
                    }}
                    onCancel={() => setRenaming(false)}
                  />
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    aria-label={folder.name}
                    aria-expanded={!folder.collapsed}
                    aria-controls={listId}
                    onClick={() => handlers.onToggleFolderCollapsed(folder.key, !folder.collapsed)}
                    className="absolute inset-0 cursor-pointer rounded-lg btn-focus-ring"
                  />
                  <span className="relative grid size-5 shrink-0 place-items-center">
                    <Folder size={17} className="group-focus-within:invisible group-hover:invisible" />
                    {custom && (
                      <span className="invisible absolute inset-0 z-10 grid place-items-center group-focus-within:visible group-hover:visible">
                        <Grip
                          label={fill(l.dragFolder, folder.name)}
                          ref={header.setActivatorNodeRef}
                          {...header.attributes}
                          {...header.listeners}
                        />
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                  <span className="flex shrink-0 items-center">
                    <span
                      className={[
                        "pointer-events-none relative z-10 grid size-6 shrink-0 place-items-center",
                        menuOpen
                          ? "pointer-events-auto"
                          : "group-focus-within:pointer-events-auto group-hover:pointer-events-auto",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "invisible",
                          menuOpen ? "visible" : "group-focus-within:visible group-hover:visible",
                        ].join(" ")}
                      >
                        <Tooltip content={l.folderMenu}>
                          <IconButton
                            icon={<MoreHorizontal />}
                            aria-label={fill(l.folderMenuAria, folder.name)}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            variant="ghost"
                            flat
                            size="sm"
                            onClick={(event) => openFrom(event.currentTarget)}
                          />
                        </Tooltip>
                      </span>
                    </span>
                    <span className="pointer-events-none grid size-6 shrink-0 place-items-center">
                      <ChevronRight
                        size={14}
                        className={["transition-transform", folder.collapsed ? "" : "rotate-90"].join(" ")}
                      />
                    </span>
                  </span>
                </>
              )}
            </div>
          )}
        </ContextMenu>
        {!folder.collapsed && (
          <div id={listId} className="space-y-1">
            {members.length === 0 && <p className="py-1 pr-3 pl-6 text-xs text-content-tertiary">{l.emptyFolder}</p>}
            <SortableContext
              items={members.map((sp) => spaceDragId(sp.key))}
              strategy={verticalListSortingStrategy}
              disabled={{ draggable: false }}
            >
              {members.map((sp) => (
                <SortableSpace
                  key={sp.key}
                  space={sp}
                  folder={folder}
                  folders={folders}
                  sortable={sortable}
                  activeSpaceKey={activeSpaceKey}
                  onOpenSpace={onOpenSpace}
                  onTogglePinSpace={onTogglePinSpace}
                  onStartNewFolder={onStartNewFolder}
                  handlers={handlers}
                  labels={l}
                  starLabel={sp.pinned ? unpinLabel : starLabel}
                  showStar={showStar}
                />
              ))}
            </SortableContext>
          </div>
        )}
      </div>
    </div>
  );
}
